import { formatMoney } from "../../core/currency";


/**
 * Why this reference shows nothing, in one sentence.
 *
 * "Nothing at your price yet" was the only thing the panel ever said, which is
 * true but useless: it reads identically whether nobody quotes the watch,
 * whether every quote is for a year the target does not cover, or whether the
 * feature is broken — and Alex reasonably concluded the last one
 * ("the target never worked", 2026-10-06).
 *
 * The reasons are ordered by how actionable they are, not by how many rows
 * each accounts for. Being quoted nothing is not something the merchant can
 * fix; a band that starts too late is one dropdown away.
 *
 * Falls back to the original sentence when the API sends no diagnostics, so a
 * page running ahead of the backend degrades to what it said before.
 */
export const explainNoHits = (diagnostics) => {
  const NOTIFY =
    "You'll get a notification the morning after a trade-group price lands at or under target.";

  if (!diagnostics) return NOTIFY;

  const {
    trade_rows_total: total = 0,
    candidates = 0,
    before_first_band: beforeBand = 0,
    above_target: above = 0,
    no_fx: noFx = 0,
    unknown_year: unknownYear = 0,
    closest_above_base: closest = null,
  } = diagnostics;

  if (total === 0) {
    return "No dealer has quoted this reference to you in a trade group yet, so there is nothing to compare a target against.";
  }

  if (candidates === 0) {
    return `${total} trade ${total === 1 ? "quote" : "quotes"} on record, but none is both auto-approved and from the last 12 months — those are the only ones a target is matched against.`;
  }

  if (above > 0) {
    const near =
      closest != null
        ? ` The closest was ${formatMoney(closest, { decimals: 0 })}.`
        : "";
    return `${above} recent ${above === 1 ? "quote is" : "quotes are"} for a year you watch, but above your target.${near}`;
  }

  if (beforeBand > 0) {
    return `${beforeBand} recent ${beforeBand === 1 ? "quote is" : "quotes are"} for a production year earlier than your first band. A band covers its own year and every later one, so add an earlier band to watch these.`;
  }

  if (unknownYear > 0) {
    return `${unknownYear} recent ${unknownYear === 1 ? "quote does" : "quotes do"} not state a production year, so they are listed below rather than counted.`;
  }

  if (noFx > 0) {
    return `${noFx} recent ${noFx === 1 ? "quote" : "quotes"} could not be converted into your base currency, so none could be compared honestly against the target.`;
  }

  return NOTIFY;
};
